// Why an anchor-watch update was refused, in the terms the dialog explains it.
//
// The anchor watch has two preconditions the user can neither see nor guess from a
// generic failure: it needs a current position to seed the swing-circle origin from,
// and that position has to come from a source precise enough to measure a swing
// circle against. Both used to reach the client as an unnamed 400, so the dialog
// could only offer "please try again" — advice that cannot work, since retrying
// without a fix fails identically. The server names them instead
// (`LogbookApiError.anchorWatchNoPosition` / `.anchorWatchGpsPrecisionTooLow`), and
// this maps those names onto the message shown.
export enum AnchorWatchFailure {
  // Anything the dialog cannot attribute — e.g. a 401 for a user without permission.
  generic = "generic",
  // No current fix at all: no NMEA position source and no native GPS.
  noPosition = "noPosition",
  // A fix exists, but its source is below high GPS precision.
  gpsPrecision = "gpsPrecision",
  // The day has not started (no log recorded), so there is nothing to attach a
  // watch to yet. Configuration edits are stored anyway -- only starting, stopping
  // or recentering is refused -- so the message says so rather than implying the
  // settings were lost (#1809).
  noDay = "noDay",
}

// Keyed on the error NAME rather than the status: both are 400s, so the status alone
// cannot tell them apart (the same reasoning as the companion connect errors).
const failures: Record<string, AnchorWatchFailure> = {
  anchorWatchNoPosition: AnchorWatchFailure.noPosition,
  anchorWatchGpsPrecisionTooLow: AnchorWatchFailure.gpsPrecision,
  anchorWatchNoDay: AnchorWatchFailure.noDay,
};

// The reason a rejected anchor-watch request names, defaulting to `generic` for
// anything unrecognised so the dialog always has something to show.
export function anchorWatchFailure(error: unknown): AnchorWatchFailure {
  if (!error || typeof error != "object") {
    return AnchorWatchFailure.generic;
  }
  const name = (error as { error?: unknown }).error;
  return (typeof name == "string" ? failures[name] : undefined) ?? AnchorWatchFailure.generic;
}
